import Cookies from 'js-cookie'

import { APP_STAGE } from './appStage'

/** Number of days until the cookie expires (refreshed on activity – sliding) */
export const AUTH_COOKIE_EXPIRY_DAYS = 7

type TStage = typeof APP_STAGE['stage'] | 'local'

/**
 * Shared contract – `admin-v2` will read/write the same cookie, so its name must match. The cookie is scoped to
 * the environment’s parent domain (e.g. `.dev.speedlo.cloud`) so that a separate admin can reuse the login.
 *
 * Mirrors that project’s `storageEnvKey` (`local`/`dev`/`stage`/`prod`),
 * which exists for exactly this kind of per-environment namespacing.
 *
 * **Why one name per environment.** Prod is served from `admin.speedlo.cloud`
 * as well as `admin.speedlo.cz`, so its cookie is scoped to `.speedlo.cloud` –
 * a DNS ancestor of dev/stage/stage2. The browser therefore *delivers* the prod
 * cookie to those hosts, and a shared name would let a lower env read the prod
 * token. Distinct names mean a lower env never asks for
 * prod’s cookie and can never read it.
 *
 * Prod keeps the bare name; only the lower environments are suffixed.
 */
export const AUTH_COOKIE_NAMES: Record<TStage, string> = {
  local: `ADMIN_AUTH_LOCAL`,
  dev: `ADMIN_AUTH_DEV`,
  stage: `ADMIN_AUTH_STAGE`,
  // `stageBeta` is the app stage; `stage2` is what the env is called everywhere
  // else (host, Helm values, pipelines), so the cookie follows the deployment
  stageBeta: `ADMIN_AUTH_STAGE2`,
  speedlopay: `ADMIN_AUTH_SPEEDLOPAY`,
  prod: `ADMIN_AUTH`,
}

/** `REACT_APP_STAGE` is unset when running locally, hence the `local` fallback */
export const authCookieName =
  AUTH_COOKIE_NAMES[APP_STAGE.stage as TStage] ?? AUTH_COOKIE_NAMES.local

export type TAuthCookie = {
  token: string
  userId: NullableID
}

/**
 * Decides who can read the auth cookie. This is what makes the login shareable
 * across an environment while still keeping environments apart, so it is worth
 * spelling out.
 *
 * A cookie's `Domain` attribute controls who receives it:
 * - omitted -> *host-only*: only the exact host that set it can read it
 * - `.dev.speedlo.cloud` -> that domain **and every subdomain under it**
 *
 * A page may only set a cookie on its own host or on a *parent* domain – never
 * on a sibling or an unrelated domain. So we derive the parent by dropping the
 * leftmost host label and prefixing a dot:
 *
 * | host                        | domain                | readable by            |
 * | --------------------------- | --------------------  | ---------------------- |
 * | `admin.dev.speedlo.cloud`   | `.dev.speedlo.cloud`  | `*.dev.speedlo.cloud`  |
 * | `admin.stage.speedlo.cloud` | `.stage.speedlo.cloud`| `*.stage.speedlo.cloud`|
 * | `admin.speedlo.cz`          | `.speedlo.cz`         | `*.speedlo.cz`         |
 * | `localhost`                 | none (host-only)      | localhost only
 *
 */
export const getCookieDomain = () => {
  const { hostname } = window.location

  const isIpAddress =
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.includes(':')

  // localhost and IP addresses are host-only, so no domain is returned
  if (hostname === 'localhost' || isIpAddress) {
    return undefined
  }

  const hostLabels = hostname.split('.')

  // need at least `subdomain.domain.tld` to have a shareable parent domain
  const hasShareableParent = hostLabels.length >= 3

  if (!hasShareableParent) {
    return undefined
  }

  // drop the leftmost label to get the parent domain
  const parentDomainLabels = hostLabels.slice(1)

  // prefix with a dot to make it shareable across subdomains
  return `.${parentDomainLabels.join('.')}`
}

/** attributes shared by set & remove – js-cookie requires them to match */
const getCookieOptions = (): Cookies.CookieAttributes => ({
  path: '/',
  domain: getCookieDomain(),
  secure: window !== undefined && window.location.protocol === 'https:',
  sameSite: 'lax', // prevents CSRF attacks while still allowing cross-origin login redirects
})

/**
 * Reads the auth cookie and returns its value, or null if absent or malformed.
 */
export const readAuthCookie = (): Nullable<TAuthCookie> => {
  const storedValue = Cookies.get(authCookieName)

  if (!storedValue) {
    return null
  }

  try {
    const parsedValue = JSON.parse(storedValue)
    const hasToken = parsedValue && typeof parsedValue.token === 'string'

    if (hasToken) {
      return { token: parsedValue.token, userId: parsedValue.userId ?? null }
    }
  } catch {
    // fall through – an unparsable value is handled the same as an unusable one
  }

  // malformed or legacy value – it can never authenticate us, so drop it rather
  // than leave a token-shaped string sitting in the browser until it expires
  removeAuthCookie()

  return null
}

/**
 * Writes the auth cookie with the given token and userId, overwriting any existing value
 *
 * The cookie is set with the specified options, including path, domain, secure flag, sameSite policy, and expiry
 */
export const writeAuthCookie = (token: string, userId: string | null) => {
  const cookieValue: TAuthCookie = { token, userId }

  Cookies.set(authCookieName, JSON.stringify(cookieValue), {
    ...getCookieOptions(),
    expires: AUTH_COOKIE_EXPIRY_DAYS,
  })
}

/**
 * Removes the auth cookie
 *
 * The cookie is removed with the same options used for setting it,
 * ensuring that it is properly deleted from the browser
 */
export const removeAuthCookie = () => {
  Cookies.remove(authCookieName, getCookieOptions())
}

/**
 * Re-writes the current cookie with a fresh expiry (sliding); no-op if absent
 * */
export const refreshAuthCookie = () => {
  const currentCookie = readAuthCookie()

  if (!currentCookie) {
    return
  }

  writeAuthCookie(currentCookie.token, currentCookie.userId)
}
